/**
 * YetkiAciklayiciPage.jsx — Sistem Ayarları › Yetki Açıklayıcı (backlog #417, 2026-10-14; tasarım #409 D11)
 * Route: /Management/SistemAyarlari/YetkiAciklayici[?firmaId=&userId=&fuarId=]   İzin: page.firma (salt okunur)
 *
 * "Bu giriş bu fuarda bu modülde NEDEN bunu yapabiliyor / yapamıyor?" Bir firma + (isteğe bağlı) o firmanın bir girişi + bir fuar
 * seçilir; backend çözücünün TÜM cevabını verir (GET /portal-rbac/aciklama): her arayüz seti için her modülün etkin seviyesi ve onu
 * SINIRLAYAN girdi — arayüz setinin tavanı (SET_CAP), girişin portal rolü (ROLE), SAKA'nın firma kısıtı (COMPANY_RESTRICTION) — ve her
 * organizatör eyleminin neden kapalı olduğu (… / ORGANIZER_SETTING). Üç girdinin kendi değerleri de gösterilir (tavan · rol · kısıt).
 * Giriş seçilmezse firma kendi başına, her seti tavanında görünür.
 *
 * "Portalı bu kullanıcı olarak önizle": #411'in salt okunur önizlemesi, bu girişin KENDİ rolleriyle (firma girişi seçilmediyse firma olarak).
 * Hiçbir şey yazılmaz.
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Table, Badge, Button, Input, Label, Row, Col, Alert, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import { getAciklama, getFirmaKullanicilari } from "../../../services/portalRbacService";
import { startOnizleme } from "../../../services/portalOnizleme";

const SEVIYE_RENK = { NONE: "light", READ: "info", WRITE: "primary", MANAGE: "success" };
const GEREKCE_RENK = { STAFF: "success", SET_CAP: "dark", ROLE: "primary", COMPANY_RESTRICTION: "danger", ORGANIZER_SETTING: "warning" };

function Seviye({ s }) {
  const { t } = useTranslation("ayarlar");
  if (!s) return <span className="text-muted">—</span>;
  return <Badge color={SEVIYE_RENK[s] || "secondary"} className={s === "NONE" ? "text-muted fw-normal" : ""}>{t(`portalArayuzleri.seviye.${s}`)}</Badge>;
}

function Gerekce({ g }) {
  const { t } = useTranslation("ayarlar");
  if (!g) return <span className="text-muted">—</span>;
  return <Badge color={GEREKCE_RENK[g] || "secondary"} data-gerekce={g}>{t(`yetkiAciklayici.gerekce.${g}`)}</Badge>;
}

export default function YetkiAciklayiciPage() {
  const { t, i18n } = useTranslation("ayarlar");
  const { t: tp } = useTranslation("portal");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { selectedFuarId } = useContext(MainContext) ?? {};
  const [firmaId, setFirmaId] = useState(params.get("firmaId") || "");
  const [userId, setUserId] = useState(params.get("userId") || "");
  const [fuarId, setFuarId] = useState(params.get("fuarId") || (selectedFuarId ? String(selectedFuarId) : ""));
  const [girisler, setGirisler] = useState([]);
  const [sonuc, setSonuc] = useState(null);
  const [hata, setHata] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  // The question being asked: set by the button, or by the link's query (Firma Detay › Portal Kullanıcıları › Neden?) on first render.
  const [sorgu, setSorgu] = useState(() => {
    const f = Number(params.get("firmaId"));
    const fu = Number(params.get("fuarId"));
    const u = Number(params.get("userId"));
    return f > 0 && fu > 0 ? { firmaId: f, userId: u > 0 ? u : undefined, fuarId: fu } : null;
  });

  useEffect(() => { document.title = `${t("yetkiAciklayici.baslik")} | FDS`; }, [t, i18n.language]);

  // Firma seçilince girişleri getir (yalnızca firma girişleri; personel burada anlamsız).
  useEffect(() => {
    let ignore = false;
    async function run() {
      const id = Number(firmaId);
      if (!Number.isFinite(id) || id <= 0) { setGirisler([]); return; }
      try {
        const v = await getFirmaKullanicilari(id);
        if (!ignore) setGirisler(v.kullanicilar.filter((k) => !k.personel));
      } catch {
        if (!ignore) setGirisler([]);
      }
    }
    run();
    return () => { ignore = true; };
  }, [firmaId]);

  const acikla = () => setSorgu({
    firmaId: Number(firmaId), userId: userId ? Number(userId) : undefined, fuarId: fuarId ? Number(fuarId) : undefined,
  });

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect). Soru değişince ya da dil değişince
  // (rol adları backend'den istek dilinde gelir) yeniden sorulur.
  useEffect(() => {
    if (!sorgu) return undefined;
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await getAciklama(sorgu);
        if (ignore) return;
        setSonuc(out);
        const p = new URLSearchParams();
        p.set("firmaId", String(sorgu.firmaId));
        if (sorgu.userId) p.set("userId", String(sorgu.userId));
        if (sorgu.fuarId) p.set("fuarId", String(sorgu.fuarId));
        setParams(p, { replace: true });
      } catch (e) {
        if (!ignore) { setSonuc(null); setHata(e.message || t("yetkiAciklayici.okunamadi")); }
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorgu, i18n.language]);

  const hedefGiris = useMemo(() => girisler.find((g) => String(g.userId) === String(userId)) ?? null, [girisler, userId]);
  const gecerli = Number(firmaId) > 0 && (Number(fuarId) > 0 || userId);

  const onizle = () => {
    startOnizleme({
      firmaId: Number(firmaId), firmaAdi: sonuc?.firma?.firmaName,
      kullaniciId: userId ? Number(userId) : undefined, kullaniciAdi: hedefGiris?.loginAdi,
    });
    navigate("/Firma/Anasayfa");
  };

  const moduller = (set) => Object.keys(set.moduller ?? {});
  const setAdi = (s) => `${t(s.yabanci ? "portalArayuzleri.yabanci" : "portalArayuzleri.yerli")} ${tp(`arayuz.${s.rol}`)}`;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("yetkiAciklayici.baslik")} pageTitle={t("yetkiAciklayici.ustBaslik")} />
        <Card>
          <CardHeader>
            <h5 className="mb-1">{t("yetkiAciklayici.baslik")}</h5>
            <div className="text-muted small">{t("yetkiAciklayici.aciklama")}</div>
          </CardHeader>
          <CardBody>
            <Row className="g-2 align-items-end" data-testid="aciklayici-form">
              <Col md={3}>
                <Label className="small mb-1">{t("yetkiAciklayici.firmaId")}</Label>
                <Input type="number" bsSize="sm" value={firmaId} data-testid="aciklayici-firma"
                  onChange={(e) => { setFirmaId(e.target.value); setUserId(""); setSonuc(null); }} />
              </Col>
              <Col md={4}>
                <Label className="small mb-1">{t("yetkiAciklayici.giris")}</Label>
                <Input type="select" bsSize="sm" value={userId} data-testid="aciklayici-giris" onChange={(e) => setUserId(e.target.value)}>
                  <option value="">{t("yetkiAciklayici.yalnizcaFirma")}</option>
                  {girisler.map((g) => <option key={g.userId} value={g.userId}>{g.loginAdi} — {[g.ad, g.soyad].filter(Boolean).join(" ")}</option>)}
                </Input>
              </Col>
              <Col md={3}>
                <Label className="small mb-1">{t("yetkiAciklayici.fuarId")}</Label>
                <Input type="number" bsSize="sm" value={fuarId} data-testid="aciklayici-fuar" onChange={(e) => setFuarId(e.target.value)} />
              </Col>
              <Col md={2}>
                <Button color="primary" size="sm" className="w-100" disabled={!gecerli || yukleniyor} onClick={acikla} data-testid="aciklayici-acikla">
                  {yukleniyor ? <Spinner size="sm" /> : t("yetkiAciklayici.acikla")}
                </Button>
              </Col>
            </Row>
            {hata && <Alert color="danger" className="mt-3 small" data-testid="aciklayici-hata">{hata}</Alert>}
          </CardBody>
        </Card>

        {sonuc && (
          <>
            <Card data-testid="aciklayici-sonuc">
              <CardBody>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <h5 className="mb-0">{sonuc.firma?.firmaName}</h5>
                  <Badge color="light" className="text-dark border">{t("yetkiAciklayici.fuar", { id: sonuc.fuarId })}</Badge>
                  {sonuc.firma?.yabanci && <Badge color="info">{t("portalArayuzleri.yabanci")}</Badge>}
                  <Badge color="secondary" data-testid="aciklayici-rolkaynagi">{t(`portalKullanicilari.kaynakAd.${sonuc.rolKaynagi}`, { defaultValue: sonuc.rolKaynagi })}</Badge>
                  <Button color="warning" outline size="sm" className="ms-auto" onClick={onizle} data-testid="aciklayici-onizle">
                    {hedefGiris ? t("yetkiAciklayici.onizleKullanici") : t("yetkiAciklayici.onizleFirma")}
                  </Button>
                </div>
                <div className="small mb-2">
                  <span className="text-muted me-1">{t("yetkiAciklayici.roller")}:</span>
                  {(sonuc.kullaniciRolleri ?? []).length === 0 && <span className="text-muted">—</span>}
                  {(sonuc.kullaniciRolleri ?? []).map((r) => <Badge color="primary" className="me-1" key={r.kod} data-rol={r.kod}>{r.ad}</Badge>)}
                </div>
                {sonuc.uyari && <Alert color="warning" className="small py-2 mb-2">{sonuc.uyari}</Alert>}
                {(sonuc.uygulananKisitlar ?? []).length > 0 && (
                  <div className="small mb-2" data-testid="aciklayici-kisitlar">
                    <span className="text-muted me-1">{t("yetkiAciklayici.kisitlar")}:</span>
                    {sonuc.uygulananKisitlar.map((k) => (
                      <Badge color="danger" className="me-1" key={k.id}>
                        {t(`portalArayuzleri.modul.${k.modul}`)} → {t(`portalArayuzleri.seviye.${k.seviye}`)} ({k.fuarId == null ? t("portalKisitlari.hepsi") : t("portalKisitlari.fuar", { id: k.fuarId })})
                      </Badge>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>

            {(sonuc.arayuzler ?? []).length === 0 && <Alert color="info">{t("yetkiAciklayici.setYok")}</Alert>}
            {(sonuc.arayuzler ?? []).map((set) => (
              <Card key={set.kod} data-set={set.kod}>
                <CardHeader><h6 className="mb-0">{setAdi(set)}</h6></CardHeader>
                <CardBody>
                  <div className="table-responsive">
                    <Table size="sm" className="align-middle mb-3">
                      <thead className="table-light">
                        <tr>
                          <th>{t("portalArayuzleri.modulBasligi")}</th><th>{t("yetkiAciklayici.seviye")}</th>
                          <th>{t("yetkiAciklayici.neden")}</th><th>{t("yetkiAciklayici.tavan")}</th><th>{t("yetkiAciklayici.rol")}</th><th>{t("yetkiAciklayici.kisit")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {moduller(set).map((m) => {
                          const g = set.gerekceler?.[m] ?? {};
                          return (
                            <tr key={m} data-modul={m} data-seviye={set.moduller[m]}>
                              <td>{t(`portalArayuzleri.modul.${m}`)}</td>
                              <td><Seviye s={set.moduller[m]} /></td>
                              <td><Gerekce g={g.gerekce} />{g.gerekce === "COMPANY_RESTRICTION" && g.kisitGerekcesi && <div className="small text-muted">{g.kisitGerekcesi}</div>}</td>
                              <td><Seviye s={g.tavan} /></td>
                              <td><Seviye s={g.rol} /></td>
                              <td><Seviye s={g.kisit} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                  {(set.eylemler ?? []).length > 0 && (
                    <>
                      <h6 className="mb-1">{t("yetkiAciklayici.eylemler")}</h6>
                      <Table size="sm" className="align-middle mb-0">
                        <thead className="table-light"><tr><th>{t("yetkiAciklayici.eylem")}</th><th>{t("yetkiAciklayici.izin")}</th><th>{t("yetkiAciklayici.neden")}</th></tr></thead>
                        <tbody>
                          {set.eylemler.map((e) => (
                            <tr key={e.kod} data-eylem={e.kod} data-izinli={e.izinli ? "true" : "false"}>
                              <td>{tp(`organizator.eylem.${e.kod}`, { defaultValue: e.ad })}</td>
                              <td>{e.izinli ? <Badge color="success">{t("yetkiAciklayici.evet")}</Badge> : <Badge color="light" className="text-muted">{t("yetkiAciklayici.hayir")}</Badge>}</td>
                              <td><Gerekce g={e.gerekce} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </CardBody>
              </Card>
            ))}

            {sonuc.birlesik && (
              <Card data-testid="aciklayici-birlesik">
                <CardHeader>
                  <h6 className="mb-0">{t("yetkiAciklayici.birlesik")}</h6>
                  <div className="text-muted small">{t("yetkiAciklayici.birlesikAciklama")}</div>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-wrap gap-2">
                    {Object.keys(sonuc.birlesik).map((m) => (
                      <span key={m} className="small border rounded px-2 py-1" data-modul={m} data-seviye={sonuc.birlesik[m].seviye}>
                        {t(`portalArayuzleri.modul.${m}`)}: <Seviye s={sonuc.birlesik[m].seviye} />
                      </span>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}
          </>
        )}
      </Container>
    </div>
  );
}
